/**
 * Embedded SVG Image Object with aspect ratio controls, smooth fading,
 * and first-class Animated WebP/GIF non-looping playback control.
 *
 * Guaranteed Behavior:
 * - When loop is false (default), plays exactly ONCE and freezes permanently on the last frame.
 * - Timeline scrub/seek support: scrubbing backward steps through earlier frames,
 *   seeking to 0 resets and allows clean replay.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface ImageProps {
  src?: string;
  href?: string;
  url?: string;
  width?: number;
  height?: number;
  preserveAspectRatio?: string;
  borderRadius?: number;
  isAnimated?: boolean;
  loop?: boolean; // Default: false (never loop!)
  freezeOnLastFrame?: boolean; // Default: true
  animationDuration?: number; // Duration in seconds
  frameCount?: number; // Total number of frames if known
  frameDuration?: number; // Duration per frame in ms
  lastFrameHref?: string; // Optional explicit static snapshot of the final frame
}

export class ImageObject extends SVGObject {
  public props: ImageProps;

  private imageEl: SVGImageElement | null = null;
  private canvasEl: HTMLCanvasElement | null = null;
  private isAnimated: boolean = false;
  private isFrozen: boolean = false;
  private decodedFrames: { canvas: HTMLCanvasElement; duration: number }[] = [];
  private isDecoding: boolean = false;
  private currentFrameIndex: number = -1;
  private baseHref: string = '';
  private lastRenderedTime: number = -1;

  constructor(config: SVGObjectConfig & { props?: ImageProps }) {
    super({ ...config, type: 'image' });
    const rawProps = (config.props || {}) as any;
    const resolvedUrl = rawProps.src || rawProps.href || rawProps.url || '';
    
    // Auto-detect animated formats if not explicitly set
    const looksAnimated = rawProps.isAnimated ?? (
      resolvedUrl.includes('.webp') ||
      resolvedUrl.includes('.gif') ||
      resolvedUrl.startsWith('data:image/webp') ||
      resolvedUrl.startsWith('data:image/gif')
    );

    this.props = {
      src: resolvedUrl,
      width: 200,
      height: 200,
      preserveAspectRatio: 'xMidYMid meet',
      borderRadius: 0,
      isAnimated: looksAnimated,
      loop: rawProps.loop ?? false, // Default: strictly play once!
      freezeOnLastFrame: rawProps.freezeOnLastFrame ?? true,
      animationDuration: rawProps.animationDuration ?? (rawProps.frameCount ? (rawProps.frameCount * (rawProps.frameDuration ?? 160)) / 1000 : 3.0),
      frameCount: rawProps.frameCount,
      frameDuration: rawProps.frameDuration ?? 160,
      lastFrameHref: rawProps.lastFrameHref,
      ...config.props
    };

    this.props.src = resolvedUrl;
    delete (this.props as any).href;
    delete (this.props as any).url;

    this.baseHref = resolvedUrl;
    this.isAnimated = !!this.props.isAnimated;

    if (this.isAnimated) {
      this.initAnimatedFrames();
    }
  }

  public render(): void {
    this.container.innerHTML = '';
    const img = Renderer.createElement('image') as SVGImageElement;
    this.imageEl = img;
    const resolvedUrl = this.props.src || (this.props as any).href || (this.props as any).url || '';
    
    Renderer.setAttributes(img, {
      x: -(this.props.width || 200) / 2,
      y: -(this.props.height || 200) / 2,
      width: this.props.width || 200,
      height: this.props.height || 200,
      href: resolvedUrl,
      preserveAspectRatio: this.props.preserveAspectRatio || 'xMidYMid meet'
    });

    // Set xlink:href for SVG spec compatibility
    img.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', resolvedUrl);
    this.container.appendChild(img);
  }

  /**
   * Initialize frame extraction using WebCodecs ImageDecoder if supported
   */
  private async initAnimatedFrames(): Promise<void> {
    if (typeof window === 'undefined' || !this.baseHref || this.isDecoding) return;
    
    // Check if WebCodecs ImageDecoder is available in browser
    const ImageDecoderCtor = (window as any).ImageDecoder;
    if (!ImageDecoderCtor) {
      return;
    }

    try {
      this.isDecoding = true;
      let arrayBuffer: ArrayBuffer;
      let mimeType = 'image/webp';

      if (this.baseHref.startsWith('data:')) {
        const matches = this.baseHref.match(/^data:([^;]+);base64,(.+)$/);
        if (matches) {
          mimeType = matches[1];
          const binary = atob(matches[2]);
          const len = binary.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          arrayBuffer = bytes.buffer;
        } else {
          return;
        }
      } else {
        const resp = await fetch(this.baseHref);
        arrayBuffer = await resp.arrayBuffer();
        if (this.baseHref.endsWith('.gif')) {
          mimeType = 'image/gif';
        }
      }

      if (ImageDecoderCtor.isTypeSupported && !(await ImageDecoderCtor.isTypeSupported(mimeType))) {
        return;
      }

      const decoder = new ImageDecoderCtor({
        data: arrayBuffer,
        type: mimeType
      });

      await decoder.tracks.ready;
      const track = decoder.tracks.selectedTrack;
      const count = track.frameCount;
      if (count <= 1) {
        // Not a multi-frame animation
        return;
      }

      const frames: { canvas: HTMLCanvasElement; duration: number }[] = [];
      for (let i = 0; i < count; i++) {
        const { image } = await decoder.decode({ frameIndex: i });
        const c = document.createElement('canvas');
        c.width = image.displayWidth;
        c.height = image.displayHeight;
        const ctx = c.getContext('2d');
        if (ctx) {
          ctx.drawImage(image, 0, 0);
        }
        frames.push({
          canvas: c,
          duration: (image.duration || 160000) / 1000000 // Convert microseconds to seconds
        });
        image.close();
      }

      if (frames.length > 0) {
        this.decodedFrames = frames;
        // Total duration is sum of frame durations
        const totalDur = frames.reduce((acc, f) => acc + f.duration, 0);
        if (totalDur > 0) {
          this.props.animationDuration = totalDur;
          this.props.frameCount = frames.length;
        }
      }
    } catch (err) {
      console.warn('[ImageObject] ImageDecoder fallback:', err);
    } finally {
      this.isDecoding = false;
    }
  }

  /**
   * Frame-by-frame 60 FPS update driven by ASAE Timeline
   */
  public update(time: number, delta: number): void {
    super.update(time, delta);
    if (!this.imageEl) return;

    if (!this.isAnimated) return;

    const elapsed = time - this.appearTime;
    const duration = this.props.animationDuration || 3.0;

    // 1. Seeking backward before appearance or resetting to 0
    if (elapsed <= 0) {
      if (this.isFrozen || this.currentFrameIndex > 0) {
        this.resetAnimation();
      }
      return;
    }

    // 2. Play Once & Freeze on Last Frame Logic
    const isPastEnd = elapsed >= duration;

    if (!this.props.loop && isPastEnd) {
      // Must permanently freeze on the final frame!
      if (!this.isFrozen) {
        this.freezeLastFrame();
      }
      return;
    }

    // 3. Active Playback: If we have decoded frames, display the exact frame for elapsed time
    if (this.decodedFrames.length > 1) {
      let targetFrame = 0;
      if (this.props.loop) {
        const loopedElapsed = elapsed % duration;
        targetFrame = Math.min(
          this.decodedFrames.length - 1,
          Math.floor((loopedElapsed / duration) * this.decodedFrames.length)
        );
      } else {
        targetFrame = Math.min(
          this.decodedFrames.length - 1,
          Math.floor((elapsed / duration) * this.decodedFrames.length)
        );
      }

      if (targetFrame !== this.currentFrameIndex) {
        this.showDecodedFrame(targetFrame);
      }
    }

    this.lastRenderedTime = time;
  }

  /**
   * Displays an extracted frame canvas directly on the SVG <image>
   */
  private showDecodedFrame(index: number): void {
    if (!this.imageEl || !this.decodedFrames[index]) return;
    this.currentFrameIndex = index;
    const frameCanvas = this.decodedFrames[index].canvas;
    const frameDataUrl = frameCanvas.toDataURL('image/png');
    this.imageEl.setAttribute('href', frameDataUrl);
    this.imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', frameDataUrl);
  }

  /**
   * Permanently freeze the image on the last frame
   */
  public freezeLastFrame(): void {
    this.isFrozen = true;
    if (!this.imageEl) return;

    // If explicit static last frame was passed, use it
    if (this.props.lastFrameHref) {
      this.imageEl.setAttribute('href', this.props.lastFrameHref);
      this.imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', this.props.lastFrameHref);
      return;
    }

    // If we have decoded frames, set the very last frame
    if (this.decodedFrames.length > 0) {
      this.showDecodedFrame(this.decodedFrames.length - 1);
      return;
    }

    // Fallback: draw current state onto canvas snapshot to freeze
    try {
      if (!this.canvasEl) {
        this.canvasEl = document.createElement('canvas');
      }
      const w = this.props.width || 200;
      const h = this.props.height || 200;
      this.canvasEl.width = w;
      this.canvasEl.height = h;
      const ctx = this.canvasEl.getContext('2d');
      if (ctx) {
        const tempImg = new Image();
        tempImg.crossOrigin = 'anonymous';
        tempImg.onload = () => {
          ctx.drawImage(tempImg, 0, 0, w, h);
          const frozenUrl = this.canvasEl!.toDataURL('image/png');
          if (this.imageEl) {
            this.imageEl.setAttribute('href', frozenUrl);
            this.imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', frozenUrl);
          }
        };
        tempImg.src = this.baseHref;
      }
    } catch {
      // Native decoder freeze with loop=1 is already active
    }
  }

  /**
   * Reset animation back to initial state for replay or seeking
   */
  public resetAnimation(): void {
    this.isFrozen = false;
    this.currentFrameIndex = -1;
    if (!this.imageEl) return;

    if (this.decodedFrames.length > 0) {
      this.showDecodedFrame(0);
    } else {
      // Re-trigger native image element by refreshing href
      const separator = this.baseHref.includes('?') ? '&' : '?';
      const refreshHref = this.baseHref.startsWith('data:') 
        ? this.baseHref 
        : `${this.baseHref}${separator}_t=${Date.now()}`;
      this.imageEl.setAttribute('href', refreshHref);
      this.imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', refreshHref);
    }
  }
}
